import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Eye, Pencil, FileText, Share2 } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import InfoTip from "@/components/InfoTip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function RoleBoard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [shareSearchId, setShareSearchId] = useState(null);
  const [shareRecipient, setShareRecipient] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [orgUsers, setOrgUsers] = useState([]);

  useEffect(() => {
    api.get("/role-searches").then(r => setSearches(r.data)).catch(console.error).finally(() => setLoading(false));
    api.get("/settings/users").then(r => setOrgUsers(r.data)).catch(() => {});
  }, [api]);

  const filtered = searches.filter(s => {
    if (filter !== "all" && s.type !== filter) return false;
    if (searchText && !s.title.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="role-board">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="text-2xl font-heading text-slate-900">Role Board</h1>
          <p className="text-sm text-slate-500 mt-1">Your active talent market searches</p>
        </div>
        <Button data-testid="new-role-search-btn" onClick={() => navigate("/search/new")} className="bg-lime-600 hover:bg-lime-700 text-white font-accent">
          <Plus className="w-4 h-4 mr-2" /> New Role Search
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 animate-slideUp">
        <div className="flex bg-white rounded-lg border p-1">
          {[["all", "All"], ["active_recruit", "Active Recruit"], ["succession_planning", "Succession"]].map(([val, label]) => (
            <button key={val} data-testid={`filter-${val}`} onClick={() => setFilter(val)}
              className={`px-3 py-1.5 text-sm font-accent rounded-md transition-colors ${filter === val ? "bg-lime-50 text-lime-700 font-medium" : "text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="search-input" value={searchText} onChange={e => setSearchText(e.target.value)}
            placeholder="Search roles..." className="pl-10" />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => <div key={i} className="h-48 rounded-xl border bg-white animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-slate-300 bg-slate-50/50 animate-fadeIn" data-testid="empty-state">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-lime-100 flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-lime-600" />
            </div>
            <h3 className="text-lg font-heading text-slate-900 mb-2">No role searches yet</h3>
            <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
              Start by creating a role search to explore the talent pool of pharmaceutical and biotech professionals.
            </p>
            <Button data-testid="create-first-search-btn" onClick={() => navigate("/search/new")} className="bg-evergreen-950 hover:bg-evergreen-900 text-white font-accent">
              Create First Role Search
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((search, idx) => (
            <Card key={search.id} className="rounded-xl border bg-card shadow hover-lift animate-slideUp" style={{ animationDelay: `${idx * 0.05}s` }} data-testid={`role-card-${search.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <StatusBadge status={search.type} />
                  <StatusBadge status={search.status} />
                </div>
                <h3 className="text-lg font-heading text-slate-900 mb-3 line-clamp-2">{search.title}</h3>
                <div className="mb-3">
                  <span className="text-4xl font-accent font-bold text-evergreen-950 animate-countUp" data-testid={`pool-count-${search.id}`}>{search.last_pool_count ?? 0}</span>
                  <span className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-1 flex items-center gap-1">
                    Candidates in pool
                    <InfoTip text="Live count of passive pharma/biotech professionals matching your saved criteria. These candidates have described their ideal next role through Varyynt's platform." side="bottom" />
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(search.criteria?.seniority_levels || []).slice(0, 2).map(s => (
                    <Badge key={s} variant="secondary" className="text-xs bg-slate-100 text-slate-700">{s}</Badge>
                  ))}
                  {(search.criteria?.functional_areas || []).slice(0, 1).map(f => (
                    <Badge key={f} variant="secondary" className="text-xs bg-slate-100 text-slate-700">{f}</Badge>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mb-4">
                  Created {new Date(search.created_at).toLocaleDateString()}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid={`view-search-${search.id}`}
                    onClick={() => navigate(`/search/${search.id}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span>View Search</span>
                  </button>
                  <button
                    data-testid={`edit-criteria-${search.id}`}
                    onClick={() => navigate(`/search/${search.id}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    <span>Edit Criteria</span>
                  </button>
                  <button
                    data-testid={`new-wo-${search.id}`}
                    onClick={() => navigate(`/work-orders/new?search=${search.id}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-lime-700 border border-lime-300 hover:bg-lime-50 rounded-md transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span>New Work Order</span>
                  </button>
                  <button
                    data-testid={`share-${search.id}`}
                    onClick={() => setShareSearchId(search.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-md transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>Share</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Share with Team Dialog */}
      <Dialog open={!!shareSearchId} onOpenChange={v => { if (!v) { setShareSearchId(null); setShareRecipient(""); setShareNote(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Share with Team</DialogTitle>
            <DialogDescription>Share this role search with a teammate in your organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Team Member</Label>
              <Select value={shareRecipient} onValueChange={setShareRecipient}>
                <SelectTrigger><SelectValue placeholder="Select teammate..." /></SelectTrigger>
                <SelectContent>{orgUsers.filter(u => u.id !== user?.id).map(u => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Note (Optional)</Label>
              <Textarea value={shareNote} onChange={e => setShareNote(e.target.value)} placeholder="Add a note..." className="min-h-[60px] text-sm" />
            </div>
            <Button disabled={!shareRecipient} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white" data-testid="share-submit"
              onClick={async () => {
                try {
                  await api.post("/share-search", { search_id: shareSearchId, recipient_id: shareRecipient, note: shareNote });
                  toast.success("Search shared with teammate");
                  setShareSearchId(null); setShareRecipient(""); setShareNote("");
                } catch (e) { toast.error(e.response?.data?.detail || "Failed to share"); }
              }}>
              <Share2 className="w-4 h-4 mr-1" /> Share Search
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
